import { Flex, Spinner, Text } from '@chakra-ui/react';
import { FieldLabel } from '../Field';
import { IFormFieldText } from '../Submission/GenericFieldComponents/Text';
import { get, useFormContext } from 'react-hook-form';
import { useValidateORCIDMutation } from 'src/api/signUpMutation';
import { useEffect, useState } from 'react';
import { FormFeedback, Input } from '@morressier/alembic';
import { validateOrcidFormat } from 'src/utils/orcIdValidator';

const ORCIDValidator = ({
  config,
  placeholder,
  setOrcidError,
  orcidError
}: {
  config: IFormFieldText;
  placeholder?: string;
  orcidError: string;
  setOrcidError: (value: string) => void;
}) => {
  const {
    register,
    formState: { errors },
    watch,
    setValue,
    trigger,
    getValues
  } = useFormContext();

  const validateORCIDMutation = useValidateORCIDMutation();
  const [isValidOrcid, setIsValidOrcid] = useState(false);

  const {
    name,
    params: { validations, labels },
    caseDataFields: { main: field }
  } = config;

  const value = watch(field) as string;

  const error = get(errors, 'orcid');

  const isLoading = validateORCIDMutation?.isLoading;

  const handleOnBlur = async () => {
    const trimmedValue = value?.trim() || '';
    setValue(field, trimmedValue);

    // Optionally clear error before async validation to reset UI
    setOrcidError('');

    if (trimmedValue) {
      const { isValid, message } = validateOrcidFormat(trimmedValue);
      if (!isValid) {
        setIsValidOrcid(false);
        setOrcidError(message);
        trigger(field);
        return;
      }
      const response = await validateORCIDMutation.mutateAsync({
        orcid: trimmedValue
      });

      if (response?.data) {
        setIsValidOrcid(response?.data?.isValidOrcid);
        setOrcidError('');
      }

      if (!response.isSuccess) {
        const data = response?.message?.data;
        setIsValidOrcid(data?.isValidOrcid);
        if (!data?.isValidOrcid) {
          setOrcidError(response?.message?.message || 'Invalid ORCID');
        }
      }
      // Trigger re-validation or UI update
      trigger(field);
    }
  };

  useEffect(() => {
    if (isLoading) {
      setOrcidError(`Validating ${name}`);
    }
  }, [isLoading, field]);

  useEffect(() => {
    setOrcidError('');
    const value = getValues(field);
    setValue(field, value);
  }, []);

  return (
    <Flex flexDirection="column" alignItems="flex-start" gap={2} width="100%">
      <FieldLabel
        label={labels.main}
        subText={labels.subtitle}
        isRequired={validations.required}
      />
      <Flex flexDirection="column" width="100%" alignItems="flex-start" gap={2}>
        <Input
          {...register(field)}
          onBlur={handleOnBlur}
          placeholder={placeholder || labels.placeholder}
          isInvalid={!!error || Boolean(orcidError)}
          defaultValue={value}
           aria-label={field}
        />
        <Flex>
          {isLoading && (
            <Flex gap={1} alignItems={'center'}>
              <Spinner size="sm" />
              <Text>{orcidError}</Text>
            </Flex>
          )}

          {!isLoading && orcidError && (
            <FormFeedback variant="error">{orcidError}</FormFeedback>
          )}
          {!isLoading && isValidOrcid && value && (
            <Flex alignItems={'center'} gap={1}>
              <FormFeedback variant="success">
                ORCID successfully verified
              </FormFeedback>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default ORCIDValidator;
